import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ConsumeMagicLinkPayload, MagicLinkConsumeResponse, SuccessType } from "./magicLinksTypes";
import magicLinksService from "./magicLinksService";

interface MagicLinksState {
    loading: boolean;
    error: string | null;
    successMessage: string | null;
    loginToken: string | null;
}

const initialState: MagicLinksState = {
    loading: false,
    error: null,
    successMessage: null,
    loginToken: null,
};

export const consumeMagicLinkThunk = createAsyncThunk<MagicLinkConsumeResponse, ConsumeMagicLinkPayload, { rejectValue: string }>(
    "magicLinks/consume",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await magicLinksService.consumeMagicLink(payload);
            return response;
        } catch (error: any) {
            const backendMessage = error.response?.data?.detail || error.response?.data?.message || "";
            let errorKey = "magic_links.errors.default";

            // Mapeamos el mensaje exacto que configuraste en tu backend en inglés
            if (backendMessage.includes("invalid")) {
                errorKey = "magic_links.errors.non_existent";
            } else if (backendMessage.includes("already")) {
                errorKey = "magic_links.errors.already_used";
            } else if (backendMessage.includes("Expired")) {
                errorKey = "magic_links.errors.expired";
            }

            return rejectWithValue(errorKey);
        }
    }
);

export const regenerateMagicLinkThunk = createAsyncThunk<SuccessType, ConsumeMagicLinkPayload, { rejectValue: string }>(
    "magicLinks/regenerate",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await magicLinksService.regenerateMagicLink(payload);
            return response;
        } catch (error: any) {
            const errorMessage = error.response?.data?.detail || error.response?.data?.message || "magic_links.errors.default";
            return rejectWithValue(errorMessage);
        }
    }
);

const magicLinksSlice = createSlice({
    name: "magicLinks",
    initialState,
    reducers: {
        resetMagicLinkState: (state) => {
            state.loading = false;
            state.error = null;
            state.successMessage = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(consumeMagicLinkThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(consumeMagicLinkThunk.fulfilled, (state, action: PayloadAction<MagicLinkConsumeResponse>) => {
                state.loading = false;
                state.successMessage = action.payload.message;
                state.loginToken = action.payload.login_token || null;
            })
            .addCase(consumeMagicLinkThunk.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "magic_links.errors.default";
                state.successMessage = null;
                state.loginToken = null;
            })
            .addCase(regenerateMagicLinkThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(regenerateMagicLinkThunk.fulfilled, (state, action: PayloadAction<SuccessType>) => {
                state.loading = false;
                state.successMessage = action.payload.message;
                state.error = null;
            })
            .addCase(regenerateMagicLinkThunk.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "magic_links.errors.default";
                state.successMessage = null;
            });
    },
});

export const { resetMagicLinkState } = magicLinksSlice.actions;
export default magicLinksSlice.reducer;